
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import getT from 'next-translate/getT';
import { GridLocation } from '@/templates';
import FlexibleContent from '@/templates/flexible-content';
import config from '@/config';
import i18n from '@/i18n';
import { getLayout } from '@/components/Layout';
import { getCities, getCommonData, getProvinces } from '@/graphql/api';
import { getAllRoutes } from '@/graphql/api/getAllRoutes';
import { client } from '@/graphql/client';
import { getLocation } from '@/graphql/queries';
import { houseTransformer } from '@/graphql/transformer/houseTransformer';
import { errorGrahpQL, getWordpressPath } from '@/graphql/utils';
import { sectionTransformer } from '@/lib/graphql/transformer/sections';
import getLocationString from '@/utils/getLocationString';
import type { TypeFlexibleContent } from '@/lib/graphql/transformer/sections';
import type { TypeHousingCardProps } from '@/organisms/housing-card';
import type { TypeGridLocationProps } from '@/templates/grid-location';
import type { GetStaticProps } from 'next';
import type { AppCommonProps } from 'pages/_app';
type PageProps = {
    flexibleContent: TypeFlexibleContent[];
    gridLocation: TypeGridLocationProps;
};
// const getLocationString = (nodes: { name: string, children?: { nodes: any[] } }[]): string => {
//   const dependencies = new Map();
//   const allLocations = new Set(nodes.map((n) => n.name));
//   // Recursive function to register parent-child relationships if the child is in the initial list
//   const registerNode = (node, parent = null) => {
//     if (!allLocations.has(node.name)) return; // Only process if it's in the initial list
//     if (!dependencies.has(node.name)) {
//       dependencies.set(node.name, new Set());
//     }
//     if (parent && allLocations.has(parent)) {
//       dependencies.get(node.name).add(parent);
//     }
//     if (
//       node.children &&
//       node.children.nodes &&
//       node.children.nodes.length > 0
//     ) {
//       node.children.nodes.forEach((child) => registerNode(child, node.name));
//     }
//   };
//   // Build dependencies only with the input nodes
//   nodes.forEach((node) => registerNode(node));
//   // Topological order only with input nodes
//   const sorted = [];
//   const visited = new Set();
//   while (allLocations.size > 0) {
//     for (const location of allLocations) {
//       if (
//         [...dependencies.get(location)].every((parent) => visited.has(parent))
//       ) {
//         sorted.push(location);
//         visited.add(location);
//         allLocations.delete(location);
//         break;
//       }
//     }
//   }
//   if (sorted.length === 0) return '';
//   if (sorted.length === 1) return sorted[0];
//   return `${sorted.at(-1)}, ${sorted[0]}`;
// };
export const Location = ({ flexibleContent, gridLocation }: PageProps) => (<>
    <GridLocation {...gridLocation}/>
    <FlexibleContent flexibleContent={flexibleContent}/>
  </>);
const getStaticProps: GetStaticProps<PageProps & AppCommonProps> = async ({ locale: onGoingLocale, params }) => {
    const { uri: uriPage } = params || {};
    const locale = onGoingLocale ?? i18n.defaultLocale;
    const location = await client.query({
        query: getLocation,
        variables: {
            slug: getWordpressPath({
                slug: uriPage,
                locale: 'es',
                path: '/donde-estamos',
            }),
        },
    });
    const data = location?.data?.location;
    errorGrahpQL(location);
    if (!data ||
        !data.language.locale.includes(locale) ||
        data.edificios.edges.length === 0)
        return { notFound: true };
    const { name, count = 0, edificios, ancestors, parent, uri, slug, databaseId: id, contenidoFlexible: { contenido: flexibleContentData = [] }, seo, contenido, } = data;
    const buildings: Array<TypeHousingCardProps & {
        subtitle: string;
    }> = [];
    edificios?.edges?.forEach(async (building: any) => {
        const buildingsTemp = await houseTransformer(building?.node?.edificio, locale, building.node.databaseId);
        const buildingLocations = building?.node?.locations?.nodes ?? [];
        const subtitle = getLocationString(buildingLocations);
        buildings.push({
            ...buildingsTemp,
            subtitle: subtitle,
        });
    });
    const { provinces } = await getProvinces(locale);
    const provinceDefaultValue: {
        id: number;
        label: string;
        href: string;
    } = ancestors
        ? ancestors.edges
            .map((location: any) => ({
            id: location?.node?.databaseId,
            label: location?.node?.name,
            href: location?.node?.uri,
        }))
            .at(-1)
        : provinces.find((location) => location.id === id);
    const cities = await Promise.all(provinces.map(async (province) => [
        province.id,
        await getCities({ id: Number(province.id) }),
    ]));
    const databaseId = data?.contenido?.edificio?.databaseId;
    const province = data?.contenido?.province;
    const flexibleContent = await sectionTransformer({
        flexibleContent: flexibleContentData,
        building: null,
        locale: locale ?? i18n.defaultLocale,
        buildingId: databaseId,
        province,
    });
    const { footer, header } = await getCommonData({
        locale: locale ?? i18n.defaultLocale,
        content: contenido,
        translations: data.translations,
    });
    const t = await getT(locale, 'location');
    return {
        props: {
            seo,
            flexibleContent,
            header,
            footer,
            gridLocation: {
                buildings,
                pretitle: t('pretitle'),
                title: t('title', { location: name }),
                description: t('description', { location: name, quantity: count }),
                ancestor: parent
                    ? {
                        label: t('viewAncestralLocation', { location: parent.node.name }),
                        href: parent.node.uri,
                    }
                    : {
                        label: t('viewAncestralLocation', { location: t('country') }),
                        href: uri.replace(new RegExp(`\/?${slug}\/?`), ''),
                    },
                searchBox: {
                    provinces: {
                        values: provinces,
                        defaultValue: provinceDefaultValue,
                    },
                    cities: {
                        citiesList: cities,
                        ...(ancestors
                            ? {
                                defaultValue: {
                                    id,
                                    label: name,
                                    href: uri,
                                },
                            }
                            : {
                                placeholder: t('city'),
                            }),
                    },
                },
            },
        },
        revalidate: config.revalidate,
        notFound: !data,
    };
};
export const getStaticPaths = async () => {
    let paths = await getAllRoutes({ type: 'locations', locale: 'es' });
    //Esto es una chapucilla para banear madrid/rivas que saca error en la build. Banear la URL arregla el fallo en la build sin consecuencias, pero muy fino no es
    //En general el sistema de URIS da problemas
    paths = paths.filter((path) => {
        const slug = Array.isArray(path.params?.uri)
            ? path.params.uri.join('/')
            : path.params?.uri;
        return slug !== 'madrid/rivas';
    });
    return {
        paths,
        fallback: 'blocking',
    };
};
Location.getLayout = getLayout;
export default Location;

    async function __Next_Translate__getStaticProps__19690d90b1e__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/donde-estamos/[...uri]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19690d90b1e__ as getStaticProps }
  